import React from 'react';
import SEO from '../components/SEO';
import '../styles/About.css';

const About = () => {
  return (
    <div className="about-page">
      <SEO 
        title="About LogiCar - Our Mission to Simplify Car Maintenance"
        description="Learn about LogiCar's mission to make car maintenance easy and accessible. Meet our team and discover why we're passionate about helping car owners."
        keywords="about LogiCar, car maintenance mission, LogiCar team, automotive app company"
        canonical="https://www.logicarapp.com/about"
      />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1>Making Car Maintenance Simple for Everyone</h1>
          <p className="hero-subtitle">We believe car care shouldn't be complicated or stressful</p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content-full">
            <h2>Our Mission</h2>
            <p>
              LogiCar was born from a simple frustration: keeping track of car maintenance shouldn't be this hard. 
              Too many car owners miss important services, leading to expensive repairs that could have been prevented.
            </p>
            <p>
              We're on a mission to change that. By combining smart technology with practical tools, we're making 
              it easy for every car owner to keep their vehicle in top condition—saving time, money, and stress.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <h2>Our Story</h2>
          <div className="story-timeline">
            <div className="timeline-item">
              <div className="timeline-icon">
                <i className="fas fa-lightbulb"></i>
              </div>
              <div className="timeline-content">
                <h3>The Problem</h3>
                <p>
                  It started with a $2,000 engine repair that could have been prevented with a simple $50 oil change. 
                  We realized that car owners needed a better way to track maintenance—something smarter than 
                  spreadsheets or relying on memory.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-icon">
                <i className="fas fa-rocket"></i>
              </div>
              <div className="timeline-content">
                <h3>The Solution</h3>
                <p>
                  We built LogiCar to be the maintenance assistant we wished we'd had. Smart reminders, AI-powered 
                  advice, and a simple interface that anyone can use. No technical knowledge required—just a 
                  tool that works.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-icon">
                <i className="fas fa-heart"></i>
              </div>
              <div className="timeline-content">
                <h3>The Future</h3>
                <p>
                  We're just getting started. Our vision is to become the go-to platform for car owners worldwide, 
                  helping millions avoid costly repairs and keep their vehicles running smoothly for years to come.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <h2>What We Stand For</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>Privacy First</h3>
              <p>Your data is yours. We use enterprise-grade encryption and never sell your information to third parties.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-user-friends"></i>
              </div>
              <h3>User-Centered</h3>
              <p>Every feature we build starts with listening to our users. Your feedback shapes our roadmap.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-brain"></i>
              </div>
              <h3>Smart Technology</h3>
              <p>We leverage AI not as a gimmick, but as a tool to provide genuinely helpful, accurate maintenance advice.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <h3>Fair Pricing</h3>
              <p>Essential features are free. Premium features are priced fairly. No hidden fees, ever.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-leaf"></i>
              </div>
              <h3>Sustainability</h3>
              <p>Well-maintained cars last longer and pollute less. We're helping reduce automotive waste.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-comments"></i>
              </div>
              <h3>Transparency</h3>
              <p>Clear communication, honest pricing, and open dialogue with our community.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="tech-section">
        <div className="container">
          <h2>Built with Modern Technology</h2>
          <p className="tech-intro">
            We use cutting-edge technology to deliver a fast, reliable, and secure experience.
          </p>
          <div className="tech-grid">
            <div className="tech-item">
              <i className="fas fa-mobile-alt"></i>
              <h4>Mobile-First</h4>
              <p>Native iOS & Android apps for the best experience</p>
            </div>
            <div className="tech-item">
              <i className="fas fa-brain"></i>
              <h4>AI-Powered</h4>
              <p>Advanced machine learning for smart recommendations</p>
            </div>
            <div className="tech-item">
              <i className="fas fa-cloud"></i>
              <h4>Cloud-Based</h4>
              <p>Your data syncs seamlessly across all devices</p>
            </div>
            <div className="tech-item">
              <i className="fas fa-lock"></i>
              <h4>Secure</h4>
              <p>Enterprise-grade encryption and data protection</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <h2>Join Us on This Journey</h2>
          <p>Be part of the movement to make car maintenance simple and stress-free.</p>
          <div className="cta-buttons">
            <button 
              onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })} 
              className="cta-button primary"
            >
              Join the Waitlist
            </button>
            <a href="mailto:reply.logicar@gmail.com" className="cta-button secondary">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

